import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Star, ArrowRight, Search, Filter, Clock, CheckCircle, XCircle, AlertCircle, Users, X } from 'lucide-react';

const MyBookings = () => {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [reviewData, setReviewData] = useState({
    rating: 5,
    title: '',
    comment: ''
  });
  const navigate = useNavigate();

  const openBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedBooking(null);
  };

  const openConfirmModal = (action, booking) => {
    setConfirmAction(action);
    setSelectedBooking(booking);
    setShowConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setSelectedBooking(null);
  };

  const executeConfirmAction = () => {
    if (confirmAction === 'modify' && selectedBooking) {
      handleModifyBooking(selectedBooking);
      closeConfirmModal();
    }
  };

  const openReviewModal = (booking) => {
    setSelectedBooking(booking);
    setReviewData({
      rating: 5,
      title: '',
      comment: ''
    });
    setShowReviewModal(true);
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
    setSelectedBooking(null);
    setReviewData({
      rating: 5,
      title: '',
      comment: ''
    });
  };

  const handleReviewSubmit = async () => {
    if (!reviewData.title.trim() || !reviewData.comment.trim()) {
      alert('Please fill in both the review title and comment.');
      return;
    }
    
    try {
      const { submitReview } = require('../api/reviews');
      await submitReview({
        bookingId: selectedBooking.id,
        propertyId: selectedBooking.property.id,
        rating: reviewData.rating,
        title: reviewData.title,
        content: reviewData.comment
      });
      alert('Thank you! Your review has been submitted successfully and is pending approval.');
      
      // Update local state to show it was reviewed
      setBookings(prev => {
        const newBookings = { ...prev };
        const past = [...newBookings.past];
        const index = past.findIndex(b => b.id === selectedBooking.id);
        if (index !== -1) {
          past[index] = { ...past[index], reviewed: true };
          newBookings.past = past;
        }
        return newBookings;
      });
      
      closeReviewModal();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit review');
    }
  };

  const handleRatingChange = (rating) => {
    setReviewData(prev => ({ ...prev, rating }));
  };

  // Handler functions
  const handlePrintDetails = (booking) => {
    const printContent = `
      ROAR HOMES - BOOKING DETAILS
      ================================
      Confirmation Number: ${booking.id}
      Property: ${booking.property.title}
      Location: ${booking.property.location}
      
      Stay Details:
      Check-in: ${booking.dates.checkIn}
      Check-out: ${booking.dates.checkOut}
      Nights: ${booking.dates.nights}
      Guests: ${booking.guests}
      
      Total Price: ${formatCurrency(booking.totalPrice)}
      Booking Date: ${booking.bookingDate}
      Status: ${booking.status}
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Roar Homes - Booking Details</title>
          <style>
            body { font-family: 'Work Sans', sans-serif; padding: 20px; line-height: 1.6; }
            h1 { font-family: 'Poppins', sans-serif; color: #1f2937; margin-bottom: 20px; }
            .header { border-bottom: 2px solid #ec4899; padding-bottom: 10px; margin-bottom: 20px; }
            .section { margin: 20px 0; }
            .total { font-weight: bold; font-size: 18px; color: #1f2937; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ROAR HOMES - BOOKING DETAILS</h1>
          </div>
          <div class="section">
            <h2>Confirmation Number</h2>
            <p>${booking.id}</p>
          </div>
          <div class="section">
            <h2>Property Information</h2>
            <p><strong>${booking.property.title}</strong></p>
            <p>${booking.property.location}</p>
          </div>
          <div class="section">
            <h2>Stay Details</h2>
            <p><strong>Check-in:</strong> ${booking.dates.checkIn}</p>
            <p><strong>Check-out:</strong> ${booking.dates.checkOut}</p>
            <p><strong>Nights:</strong> ${booking.dates.nights}</p>
            <p><strong>Guests:</strong> ${booking.guests}</p>
          </div>
          <div class="section">
            <h2>Payment Information</h2>
            <p class="total">Total Price: ${formatCurrency(booking.totalPrice)}</p>
          </div>
          <div class="section">
            <h2>Booking Information</h2>
            <p><strong>Booking Date:</strong> ${booking.bookingDate}</p>
            <p><strong>Status:</strong> ${booking.status}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleModifyBooking = (booking) => {
    // In a real app, this would navigate to booking modification page
    alert('Booking modification feature coming soon! Please contact support@roar.com for immediate assistance.');
    // navigate(`/modify-booking/${booking.id}`);
  };

  const handleContactHost = (booking) => {
    const subject = encodeURIComponent(`Regarding Booking ${booking.id} - ${booking.property.title}`);
    const body = encodeURIComponent(`Hi,\n\nI'm writing regarding my upcoming booking (${booking.id}) at ${booking.property.title} from ${booking.dates.checkIn} to ${booking.dates.checkOut}.\n\nPlease let me know if you have any questions or special instructions for my stay.\n\nThank you!`);
    window.location.href = `mailto:support@roar.com?subject=${subject}&body=${body}`;
  };

  const handleBookAgain = (booking) => {
    navigate(`/property/${booking.property.id}`);
  };

  const [bookings, setBookings] = useState({ upcoming: [], past: [], cancelled: [] });
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    const fetchBookings = async () => {
      try {
        const { getMyBookings } = require('../api/bookings');
        const data = await getMyBookings();
        if (data && Array.isArray(data)) {
          const categorized = { upcoming: [], past: [], cancelled: [] };
          data.forEach(b => {
            const nightlyTotal = parseFloat(b.totalAmount);
            const securityDeposit = parseFloat(b.Property?.securityDeposit || 0);
            const nights = Math.max(1, Math.ceil((new Date(b.checkOutDate) - new Date(b.checkInDate)) / (1000 * 60 * 60 * 24)));
            const vat = nightlyTotal * 0.075;
            const finalTotal = nightlyTotal + securityDeposit + vat;

            const bookingModel = {
              id: b.id,
              property: {
                id: b.propertyId,
                title: b.Property?.name || 'Property',
                image: (b.Property?.images && b.Property.images.length > 0) ? b.Property.images[0] : 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=400&h=300&fit=crop',
                location: b.Property?.city ? `${b.Property.city}, ${b.Property.country || ''}` : b.Property?.address || 'Location',
                rating: b.Property?.rating || 0
              },
              dates: {
                checkIn: b.checkInDate,
                checkOut: b.checkOutDate,
                nights
              },
              guests: b.numberOfGuests,
              nightlyTotal,
              securityDeposit,
              vat,
              totalPrice: finalTotal,
              status: b.status,
              bookingDate: b.createdAt,
              paymentStatus: b.paymentStatus || 'paid',
              reviewed: false
            };

            if (b.status === 'cancelled') {
              categorized.cancelled.push(bookingModel);
            } else if (b.status === 'completed' || new Date(b.checkOutDate) < new Date()) {
              bookingModel.status = 'completed';
              categorized.past.push(bookingModel);
            } else {
              bookingModel.status = 'confirmed';
              categorized.upcoming.push(bookingModel);
            }
          });
          setBookings(categorized);
        }
      } catch (err) {
        console.error('Failed to fetch bookings', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, []);

  const filteredBookings = bookings[activeTab]?.filter(booking =>
    booking.property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    booking.property.location.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const getStatusBadge = (status) => {
    const badges = {
      confirmed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Confirmed' },
      completed: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle, text: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Cancelled' }
    };
    const badge = badges[status] || badges.confirmed;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.text}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/account" className="text-gray-600 hover:text-gray-900 mr-4">
                <ArrowRight className="rotate-180" size={20} />
              </Link>
              <h1 className="text-xl font-semibold text-gray-900">My Bookings</h1>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search bookings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {['upcoming', 'past', 'cancelled'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab}
                <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                  {bookings[tab]?.length || 0}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="text-center py-12">
            <svg className="animate-spin h-8 w-8 text-pink-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="mt-4 text-gray-600">Loading your bookings...</p>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Calendar className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No {activeTab} bookings</h3>
            <p className="text-gray-600 mb-6">
              {activeTab === 'upcoming' 
                ? "You don't have any upcoming bookings. Start exploring properties!"
                : activeTab === 'past'
                ? "You haven't completed any stays yet."
                : "You haven't cancelled any bookings."
              }
            </p>
            {activeTab === 'upcoming' && (
              <Link
                to="/properties"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600"
              >
                Explore Properties
                <ArrowRight className="ml-2" size={16} />
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {filteredBookings.map((booking) => (
              <div key={booking.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="flex flex-wrap justify-center ">
                  {/* Property Image */}
                  <div className="md:w-1/4">
                    <img
                      src={booking.property.image}
                      alt={booking.property.title}
                      className="w-full h-full  md:h-full object-cover"
                    />
                  </div>

                  {/* Booking Details */}
                  <div className="flex-1 p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {booking.property.title}
                        </h3>
                        <div className="flex items-center text-gray-600 text-sm mb-2">
                          <MapPin className="w-4 h-4 mr-1" />
                          {booking.property.location}
                        </div>
                        <div className="flex items-center text-gray-600 text-sm">
                          <Star className="w-4 h-4 text-yellow-400 mr-1" />
                          {booking.property.rating}
                        </div>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(booking.status)}
                        <p className="text-sm text-gray-500 mt-1">ID: {booking.id}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="w-4 h-4 mr-2" />
                        {formatDate(booking.dates.checkIn)} - {formatDate(booking.dates.checkOut)}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="w-4 h-4 mr-2" />
                        {booking.dates.nights} nights
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium">{booking.guests}</span> guests
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                      <div>
                        <p className="text-sm text-gray-500">Total paid</p>
                        <p className="text-xl font-semibold text-gray-900">{formatCurrency(booking.totalPrice)}</p>
                      </div>
                      <div className="flex space-x-3">
                        {booking.status === 'confirmed' && (
                          <>
                            <button 
                              onClick={() => openBookingDetails(booking)}
                              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                            >
                              View Details
                            </button>
                            <button 
                          onClick={() => handleContactHost(booking)}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                        >
                          Contact Host
                        </button>
                          </>
                        )}
                        {booking.status === 'completed' && !booking.reviewed && (
                          <button 
                          onClick={() => openReviewModal(booking)}
                          className="px-4 py-2 bg-gradient-to-r from-pink-500 to-red-500 text-white rounded-lg hover:from-pink-600 hover:to-red-600 text-sm"
                        >
                          Leave Review
                        </button>
                        )}
                        {booking.status === 'completed' && booking.reviewed && (
                          <button 
                          onClick={() => handleBookAgain(booking)}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                        >
                          Book Again
                        </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Booking Details Modal */}
      {showDetailsModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl max-h-[90vh] overflow-y-auto w-full">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900">Booking Details</h3>
              <button
                onClick={closeDetailsModal}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Property Information */}
              <div className="flex flex-col md:flex-row gap-6 mb-6">
                <div className="md:w-1/2">
                  <img
                    src={selectedBooking.property.image}
                    alt={selectedBooking.property.title}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
                <div className="md:w-1/2">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{selectedBooking.property.title}</h4>
                  <div className="flex items-center text-gray-600 mb-4">
                    <MapPin className="w-5 h-5 mr-2" />
                    {selectedBooking.property.location}
                  </div>
                  <div className="flex items-center text-gray-600 mb-4">
                    <Star className="w-5 h-5 text-yellow-400 mr-2" />
                    <span className="font-semibold">{selectedBooking.property.rating}</span>
                    <span className="text-gray-500 ml-2">(Reviews)</span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Experience luxury and comfort in this stunning property. Perfect for families and groups looking for an unforgettable getaway.
                  </p>
                </div>
              </div>

              {/* Booking Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-semibold text-gray-900 mb-3">Dates & Guests</h5>
                  <div className="space-y-2">
                    <div className="flex items-center text-gray-700">
                      <Calendar className="w-5 h-5 mr-3 text-pink-500" />
                      <span>{formatDate(selectedBooking.dates.checkIn)} - {formatDate(selectedBooking.dates.checkOut)}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Clock className="w-5 h-5 mr-3 text-pink-500" />
                      <span>{selectedBooking.dates.nights} nights</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Users className="w-5 h-5 mr-3 text-pink-500" />
                      <span>{selectedBooking.guests} guests</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-semibold text-gray-900 mb-3">Payment Information</h5>
                  <div className="space-y-2">
                    <div className="flex justify-between text-gray-700">
                      <span>{formatCurrency(Math.round(selectedBooking.nightlyTotal / selectedBooking.dates.nights))} × {selectedBooking.dates.nights} nights</span>
                      <span>{formatCurrency(selectedBooking.nightlyTotal)}</span>
                    </div>
                    {selectedBooking.securityDeposit > 0 && (
                      <div className="flex justify-between text-gray-700">
                        <span>Security Deposit (Refundable)</span>
                        <span>{formatCurrency(selectedBooking.securityDeposit)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-gray-700">
                      <span>VAT (7.5%)</span>
                      <span>{formatCurrency(selectedBooking.vat)}</span>
                    </div>
                    <div className="flex justify-between text-gray-700 font-semibold border-t border-gray-200 pt-2 mt-2">
                      <span>Total Paid</span>
                      <span className="text-gray-900">{formatCurrency(selectedBooking.totalPrice)}</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>Payment Status:</span>
                      <span className="font-semibold text-green-600">Paid</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>Booking ID:</span>
                      <span className="font-mono text-sm">{selectedBooking.id}</span>
                    </div>
                  </div>
                </div>
              </div>


              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button 
                    onClick={() => handlePrintDetails(selectedBooking)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Print Details
                  </button>
                <button 
                    onClick={() => openConfirmModal('modify', selectedBooking)}
                    className="px-6 py-3 bg-gradient-to-r from-pink-500 to-red-500 text-white rounded-lg hover:from-pink-600 hover:to-red-600"
                  >
                    Modify Booking
                  </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                {confirmAction === 'modify' ? 'Modify Booking' : 'Leave Review'}
              </h3>
              <button 
                onClick={closeConfirmModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-2">
                {confirmAction === 'modify' 
                  ? `Do you want to modify your booking for "${selectedBooking.property.title}"?`
                  : `Do you want to leave a review for "${selectedBooking.property.title}"?`
                }
              </p>
              <p className="text-sm text-gray-500">
                {confirmAction === 'modify'
                  ? 'This will take you to the booking modification page where you can change dates, guests, or other details.'
                  : 'This will take you to the review page where you can share your experience.'
                }
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button 
                onClick={closeConfirmModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={executeConfirmAction}
                className="px-4 py-2 bg-gradient-to-r from-pink-500 to-red-500 text-white rounded-lg hover:from-pink-600 hover:to-red-600"
              >
                {confirmAction === 'modify' ? 'Modify' : 'Leave Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Leave a Review</h3>
              <button 
                onClick={closeReviewModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Reviewing: <span className="font-semibold">{selectedBooking.property.title}</span>
              </p>
              <p className="text-xs text-gray-500">
                Booking ID: {selectedBooking.id}
              </p>
            </div>

            {/* Rating Stars */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
              <div className="flex space-x-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => handleRatingChange(star)}
                    className="p-1 transition-transform hover:scale-110 active:scale-95"
                  >
                    <Star
                      size={28}
                      className={star <= reviewData.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300 fill-none"}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Review Title */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Review Title</label>
              <input
                type="text"
                value={reviewData.title}
                onChange={(e) => setReviewData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Summarize your experience"
                maxLength={100}
              />
            </div>

            {/* Review Comment */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Review</label>
              <textarea
                value={reviewData.comment}
                onChange={(e) => setReviewData(prev => ({ ...prev, comment: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                rows={4}
                placeholder="Share details about your stay..."
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">
                {reviewData.comment.length}/500 characters
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              <button 
                onClick={closeReviewModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={handleReviewSubmit}
                className="px-4 py-2 bg-gradient-to-r from-pink-500 to-red-500 text-white rounded-lg hover:from-pink-600 hover:to-red-600"
              >
                Submit Review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyBookings;
